import crossFetch from 'cross-fetch';
import { ResponseError } from '@backstage/errors';
import { SyntaxHighlighterApi } from './SyntaxHighlighterApi';
import {
  syntaxHighlighterInfoSchema,
  SyntaxHighlighterQuery,
  syntaxHighlighterOutputSchema,
} from '../types';

// these are isomorphic types that don't require @backstage/core-plugin-api
type DiscoveryApi = { getBaseUrl(pluginId: string): Promise<string> };
type FetchApi = { fetch: typeof fetch };

export class SyntaxHighlighterClient implements SyntaxHighlighterApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  public constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi?: FetchApi;
  }) {
    const { discoveryApi, fetchApi } = options;
    this.discoveryApi = discoveryApi;
    this.fetchApi = fetchApi ?? { fetch: crossFetch };
  }

  public async getInfo() {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(baseUrl);
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    const data = await response.json();
    return syntaxHighlighterInfoSchema.parse(data);
  }

  public async highlight(query: SyntaxHighlighterQuery) {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(baseUrl, {
      method: 'POST',
      body: JSON.stringify(query),
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
      },
    });
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    const data = await response.json();
    return syntaxHighlighterOutputSchema.parse(data);
  }

  private async getBaseUrl() {
    return this.discoveryApi.getBaseUrl('syntax-highlighter');
  }
}
